import React from "react";
import { useNavigate } from "react-router-dom";
import { toCollegeDetailsPath } from "../utility/collegeUrl";
import { withImageFallback } from "../utility/imageFallback";
import {
  Award,
  Check,
  Heart,
  IndianRupee,
  MapPin,
  Star,
  TrendingUp,
} from "lucide-react";
import CompareToggle from "./CompareToggle";

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  const cleaned = String(value).replace(/,/g, "").replace(/₹/g, "");
  const match = cleaned.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const num = Number(match[1]);
  return Number.isFinite(num) ? num : null;
};

const formatLakhsRange = (min, max) => {
  if (!Number.isFinite(min) && !Number.isFinite(max)) return null;
  const toL = (v) => v / 100000;
  const minL = Number.isFinite(min) ? toL(min) : null;
  const maxL = Number.isFinite(max) ? toL(max) : null;
  const fmt = (v) => `${(Math.round(v * 10) / 10).toFixed(1)}L`;
  if (minL !== null && maxL !== null) return `₹${fmt(minL)} – ${fmt(maxL)}`;
  if (minL !== null) return `₹${fmt(minL)}`;
  if (maxL !== null) return `₹${fmt(maxL)}`;
  return null;
};

const getFeeRangeText = (college) => {
  const courses = Array.isArray(college?.courses) ? college.courses : [];
  const fees = courses.map((c) => toNumber(c?.tuitionFees)).filter((n) => n !== null);
  if (fees.length === 0) return null;
  const min = Math.min(...fees);
  const max = Math.max(...fees);
  return formatLakhsRange(min, max);
};

const HomeCollegeCarouselCard = ({ college }) => {
  const navigate = useNavigate();
  const title = String(college?.collegeName || "—").trim() || "—";
  const location = [college?.city, college?.state].filter(Boolean).join(", ") || "—";

  const collegeUrl = toCollegeDetailsPath(college?.collegeName, college?.city, college?.id);
  const fallbackBanner = "/images/college-banner.jpg";
  const fallbackLogo = "/images/college-logo.jpeg";

  const bannerSrc =
    college?.bannerImage && college.bannerImage !== "N/A" ? college.bannerImage : fallbackBanner;
  const logoSrc =
    college?.collegeLogo && college.collegeLogo !== "N/A" ? college.collegeLogo : fallbackLogo;

  const ratingValue = Number.isFinite(Number(college?.ratings)) ? Number(college.ratings) : 0;

  const reviewCount = Number.isFinite(Number(college?.reviews)) ? Number(college.reviews) : null;

  const nirfRankText = Number.isFinite(Number(college?.collegeRank))
    ? `#${Number(college.collegeRank)}`
    : college?.nirf
      ? `#${String(college.nirf)}`
      : "—";

  const naacText =
    college?.naac &&
    String(college.naac).trim() &&
    String(college.naac).trim().toUpperCase() !== "N/A"
      ? String(college.naac).trim()
      : college?.currentGrade &&
          String(college.currentGrade).trim() &&
          String(college.currentGrade).trim().toUpperCase() !== "N/A"
        ? String(college.currentGrade).trim()
        : "—";

  const placementText =
    college?.placementRate &&
    String(college.placementRate).trim() &&
    String(college.placementRate).trim().toUpperCase() !== "N/A"
      ? String(college.placementRate).trim()
      : "—";

  const feeBubbleText = getFeeRangeText(college);
  const feeRangeText = feeBubbleText || "—";

  return (
    <div className="px-0 h-full">
      <div
        role="button"
        tabIndex={0}
        className="h-full rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer"
        onClick={() => navigate(collegeUrl)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(collegeUrl);
          }
        }}
      >
        <div className="relative">
          <img
            src={bannerSrc}
            alt={title}
            className="h-28 sm:h-36 md:h-48 w-full object-cover"
            onError={withImageFallback(fallbackBanner)}
            loading="lazy"
          />

          {/* Rank badge */}
          <div className="absolute left-3 top-3">
            <div className="rounded-xl bg-amber-100 border border-amber-200 px-3 py-2 text-amber-900">
              <div className="text-lg font-extrabold leading-none">{nirfRankText}</div>
              <div className="text-[10px] font-semibold tracking-wide">in INDIA</div>
            </div>
          </div>

          {/* Fee bubble (only when we actually have fee data — no point showing an empty "—" pill) */}
          {feeBubbleText && (
            <div className="absolute right-3 top-3">
              <div className="rounded-xl bg-white/90 border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900">
                {feeBubbleText}
              </div>
            </div>
          )}

          {/* Logo */}
          <div className="absolute -bottom-6 left-4">
            <div className="h-14 w-14 rounded-2xl bg-white border border-slate-200 shadow grid place-items-center overflow-hidden">
              <img
                src={logoSrc}
                alt={title}
                className="h-12 w-12 object-contain"
                onError={withImageFallback(fallbackLogo)}
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="p-3 md:p-4 pt-10">
          <div>
            <div
              title={title}
              className="min-h-[3.5rem] md:min-h-[4.25rem] text-xl md:text-2xl font-extrabold text-slate-900 leading-snug pr-1 pt-0.5 break-words line-clamp-2"
            >
              {title}
            </div>
            <div className="mt-1 flex items-center gap-1 text-sm text-slate-600">
              <MapPin className="h-4 w-4 flex-none" />
              <span className="leading-snug">{location}</span>
            </div>

            <div className="mt-3 flex justify-start">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-slate-50"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(collegeUrl);
                }}
              >
                Get Details
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
              <Award className="h-4 w-4 text-indigo-600" />
              <div className="min-w-0 text-xs sm:text-sm text-slate-700 leading-snug">
                <span className="text-slate-500">NIRF:</span>{" "}
                <span className="font-semibold">{nirfRankText}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
              <Check className="h-4 w-4 text-emerald-600" />
              <div className="min-w-0 text-xs sm:text-sm text-slate-700 leading-snug">
                <span className="text-slate-500">NAAC:</span>{" "}
                <span className="font-semibold">{naacText}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
              <IndianRupee className="h-4 w-4 text-amber-600" />
              <div className="min-w-0 text-xs sm:text-sm text-slate-700 leading-snug">
                <span className="text-slate-500">Fees:</span>{" "}
                <span className="font-semibold">{feeRangeText}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
              <TrendingUp className="h-4 w-4 text-orange-600" />
              <div className="min-w-0 text-xs sm:text-sm text-slate-700 leading-snug">
                <span className="text-slate-500">Placements:</span>{" "}
                <span className="font-semibold">{placementText}</span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
              <span className="text-base font-semibold">
                {(Math.round(ratingValue * 10) / 10).toFixed(1)}
              </span>
              {reviewCount !== null ? (
                <span className="text-sm text-slate-500">({reviewCount})</span>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <CompareToggle college={college} />
              <button
                type="button"
                aria-label="Save college"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition hover:border-rose-200 hover:text-rose-500"
              >
                <Heart className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeCollegeCarouselCard;
